import React, { useEffect, useState } from "react";
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList
} from "recharts";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";
import DataTable from "react-data-table-component";
import { API_BASE } from "./config";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

export default function AbsensiDashboard() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];  
  });
  const [totalKaryawan, setTotalKaryawan] = useState(0);
  const [clockInData, setClockInData] = useState([]);
  const [clockOutData, setClockOutData] = useState([]);
  const [deptData, setDeptData] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [notOnTimeOut, setNotOnTimeOut] = useState([]);
  const [specialCasesClockIn, setSpecialCasesClockIn] = useState([]);
  const [specialCasesClockOut, setSpecialCasesClockOut] = useState([]);
  const [searchClockIn, setSearchClockIn] = useState("");
  const [searchClockOut, setSearchClockOut] = useState("");

  const columns = [
    { name: "Nama", selector: (row) => row.nama, sortable: true },
    { name: "Departemen", selector: (row) => row.dept, sortable: true },
    { name: "Tanggal", selector: (row) => row.tanggal, sortable: true },
    { 
      name: "Keterangan", 
      selector: (row) => row.keterangan,
      cell: (row) => (
        <span className={
          row.keterangan === "Belum Clock In" || row.keterangan === "Belum Clock Out"
            ? "text-red-500 font-semibold"
            : "text-blue-600 font-semibold"
        }>
          {row.keterangan}
        </span>
      )
    }
  ];

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard?date=${selectedDate}`);
  
        setTotalKaryawan(res.data.totalKaryawan);

        setClockInData((res.data.clockInDist || []).map(r => ({
          name: r.status,
          value: r.jumlah
        })));
  
        setClockOutData((res.data.clockOutDist || []).map(r => ({
          name: r.status,
          value: r.jumlah
        })));
  
        setDeptData((res.data.deptData || []).map(r => ({
          dept: r.dept,
          clockIn: r.clockIn,   
          clockOut: r.clockOut
        })));
  
        setLateEmployees(res.data.lateEmployees || []);
        setNotOnTimeOut(res.data.notOnTimeOut || []);
        setSpecialCasesClockIn(res.data.specialCasesClockIn || []);
        setSpecialCasesClockOut(res.data.specialCasesClockOut || []);
      } catch (err) {
        console.error("Error fetch dashboard:", err);
      }
    };
  
    fetchDashboard();
  }, [selectedDate]);
  
    const filteredClockIn = specialCasesClockIn.filter((item) =>
    item.nama?.toLowerCase().includes(searchClockIn.toLowerCase()) ||
    item.dept?.toLowerCase().includes(searchClockIn.toLowerCase()) ||
    item.keterangan?.toLowerCase().includes(searchClockIn.toLowerCase())
    );

    const filteredClockOut = specialCasesClockOut.filter((item) =>
    item.nama?.toLowerCase().includes(searchClockOut.toLowerCase()) ||
    item.dept?.toLowerCase().includes(searchClockOut.toLowerCase()) ||
    item.keterangan?.toLowerCase().includes(searchClockOut.toLowerCase())
    );

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="bg-blue-600 text-white p-6 rounded-2xl shadow text-center text-xl font-bold">
          Total Karyawan: {totalKaryawan ?? 0}

        </div>
        <div>
          <label className="mr-2 font-medium">Pilih Tanggal:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold mb-4">Diagram Clock In</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={clockInData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {(clockInData|| []).map((entry, index) => (
                    <Cell key={`cell-in-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold mb-4">Diagram Clock Out</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={clockOutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {(clockOutData|| []).map((entry, index) => (
                    <Cell key={`cell-out-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold mb-4">Distribusi Penyimpangan Absensi per Departemen</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={deptData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
              <XAxis dataKey="dept" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value} orang`, "Jumlah"]} />
              <Legend />
              <Bar dataKey="clockOut" fill="#F59E0B" name="Pulang Lebih Awal">
                <LabelList dataKey="clockOut" position="top" formatter={(v) => `${v}`} />
              </Bar>
              <Bar dataKey="clockIn" fill="#4F46E5" name="Terlambat Datang">
                <LabelList dataKey="clockIn" position="top" formatter={(v) => `${v}`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold mb-4">Karyawan Terlambat (Clock In)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-center border-b">
                  <th>Nama</th><th>Departemen</th><th>Tanggal</th><th>Jam</th>
                </tr>
              </thead>
              <tbody>
                {(lateEmployees|| []).map((emp, i) => (
                  <tr key={i} className="text-center border-t">
                    <td>{emp.nama}</td>
                    <td>{emp.dept}</td>
                    <td>{emp.tanggal}</td>
                    <td>{emp.jamMasuk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h3 className="text-lg font-semibold mb-4">Karyawan Pulang Tidak Tepat Waktu (Clock Out)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-center border-b">
                  <th>Nama</th>
                  <th>Departemen</th>
                  <th>Tanggal</th>
                  <th>Jam Pulang</th>
                </tr>
              </thead>
              <tbody>
                {(notOnTimeOut|| []).map((emp, i) => (
                  <tr key={i} className="text-center border-t">
                    <td>{emp.nama}</td>
                    <td>{emp.dept}</td>
                    <td>{emp.tanggal}</td>
                    <td>{emp.jamPulang}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
  <Card className="shadow-md rounded-2xl">
    <CardContent className="p-6">
      <h3 className="text-xl font-bold mb-4 text-gray-700">
        Data Cuti / Izin / Sakit / Belum Clock In
      </h3>
        <div className="mb-2">
        <input
            type="text"
            placeholder="🔍 Cari Nama / Departemen / Keterangan"
            value={searchClockIn}
            onChange={(e) => setSearchClockIn(e.target.value)}
            className="border px-3 py-2 rounded w-80 md:w-96"
        />
        </div>
      <DataTable
        columns={columns}
        data={filteredClockIn}
        pagination
        highlightOnHover
        striped
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
      />
    </CardContent>
  </Card>

  <Card className="shadow-md rounded-2xl">
    <CardContent className="p-6">
      <h3 className="text-xl font-bold mb-4 text-gray-700">
        Data Pulang Tidak Tepat Waktu / Belum Clock Out
      </h3>
        <div className="mb-2">
        <input
        type="text"
        placeholder="🔍 Cari Nama / Departemen / Keterangan"
        value={searchClockOut}
        onChange={(e) => setSearchClockOut(e.target.value)}
        className="border px-3 py-2 rounded w-80 md:w-96"
        />

        </div>
      <DataTable
        columns={columns}
        data={filteredClockOut}
        pagination
        highlightOnHover
        striped
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
      />
    </CardContent>
  </Card>
</div>


    </div>
  );
}
